// Google OAuth 2.0 Device Authorization Grant (RFC 8628) — qua BFF `auth-device`.
//
// Why Device Flow: Avatar is a terminal CLI with no browser redirect URL.
// The user logs in via google.com/device on a browser and the CLI polls
// for the resulting token.
//
// v3.16.0 (plan 260701-1941): CLI KHÔNG còn ôm client secret. Mọi call OAuth
// (device code / poll / refresh / revoke) đi qua Edge Function `auth-device`
// trên avatar-supabase — secret nằm ở Supabase secrets (NAL_GOOGLE_CLIENT_SECRET).
// BFF passthrough NGUYÊN VĂN response Google (status + JSON body) nên toàn bộ
// logic parse/error-mapping trong file này giữ nguyên như thời gọi Google trực tiếp.
//
// Rotate secret giờ là việc server-side: Console Reset Secret → `supabase
// secrets set NAL_GOOGLE_CLIENT_SECRET=...` → xong. KHÔNG cần release CLI.

import type { UserConfig } from "../types/config-schema.js";
import { resolveAuthDeviceEndpoint } from "./resolve-auth-device-endpoint.js";

// Client ID là PUBLIC identifier (không phải secret) — GIỮ trong CLI để verify
// claim `aud` của id_token (verifyIdTokenClaims bên dưới).
//
// ⚠️ NGUỒN DUY NHẤT của chuỗi này trong repo. Đổi OAuth client trên Console thì
// phải cập nhật ĐỦ 3 nơi, thiếu 1 là hỏng im lặng:
//   1. hằng dưới đây (test import lại từ đây, không chép literal);
//   2. Supabase secrets NAL_GOOGLE_CLIENT_ID / NAL_GOOGLE_CLIENT_SECRET (edge fn auth-device);
//   3. env `AVATAR_CLI_CLIENT_ID` phía telemetry — lệch ⇒ MỌI POST /api/telemetry → 401
//      (xem telemetry/PRE_DEPLOY.md §2 Nhóm A).
export const GOOGLE_CLIENT_ID =
  "1014766441755-i4jimivh5rd7vt8phuhmepmt45sovtph.apps.googleusercontent.com";

// Restrict login tới các Google Workspace của NAL. Hỗ trợ NHIỀU workspace độc lập
// (nal.vn và nal-software.com là 2 Google Workspace RIÊNG BIỆT).
//
// Bảo mật nằm ở verify `id_token.hd` claim — claim này do Google ký, chỉ gán cho
// account thuộc một Workspace thật (Gmail thường KHÔNG có hd). KHÔNG verify bằng
// email.endsWith (string dễ nhầm). Account hợp lệ khi hd ∈ ALLOWED_HOSTED_DOMAINS.
//
// KHÔNG còn hd hint trong verification URL: với nhiều workspace, hint chỉ-1-domain
// sẽ lọc mất account của workspace kia. Bỏ hint → Google hiện mọi account, user tự
// chọn; verify hd ở bước sau vẫn chặn account ngoài danh sách (hint chỉ là UX,
// không phải security boundary).
export const ALLOWED_HOSTED_DOMAINS = ["nal.vn", "nal-software.com"];

// Backward-compat: vài chỗ/test cũ tham chiếu HOSTED_DOMAIN (domain chính NAL).
export const HOSTED_DOMAIN = ALLOWED_HOSTED_DOMAINS[0];

// Scope (openid email profile) do BFF quyết server-side — client không gửi scope.

// POST JSON tới BFF auth-device. Endpoint resolve MỖI lần gọi (env/workspace
// override có thể đổi giữa test/dev session). Body là JSON, KHÔNG form-encoded
// như Google — BFF nhận JSON rồi tự build form + gắn client_id/secret gửi Google.
async function postToAuthBff(
  path: "start" | "poll" | "refresh" | "revoke",
  body: Record<string, string>,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(`${resolveAuthDeviceEndpoint()}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
}

export interface DeviceCodeResponse {
  device_code: string;
  user_code: string;
  verification_url: string;
  expires_in: number;
  interval: number;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

export interface IdTokenClaims {
  email: string;
  email_verified: boolean;
  name?: string;
  hd?: string;
  exp: number;
  iss: string;
  aud: string;
}

// ── Step 2 of Command 01 spec: request device + user codes (qua BFF).
export async function requestDeviceCode(): Promise<DeviceCodeResponse> {
  const res = await postToAuthBff("start", {});
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Device code request failed (${res.status}): ${text}`);
  }
  return (await res.json()) as DeviceCodeResponse;
}

// ── Step 5: poll the token endpoint until user authorises or expiry.
// Returns the token response on success, null while still pending.
// Throws on hard errors (access_denied, expired_token).
export async function pollForToken(deviceCode: string): Promise<TokenResponse | null> {
  const res = await postToAuthBff("poll", { device_code: deviceCode });

  if (res.ok) {
    return (await res.json()) as TokenResponse;
  }

  // Google returns 4xx with a JSON {error} field for both pending and fatal states.
  let errorCode = "";
  try {
    const data = (await res.json()) as { error?: string };
    errorCode = data.error ?? "";
  } catch {
    errorCode = "";
  }

  // rate_limited: BFF trả 429 (per-IP limiter) — coi như slow_down, chờ nhịp poll sau.
  if (
    errorCode === "authorization_pending" ||
    errorCode === "slow_down" ||
    errorCode === "rate_limited"
  ) {
    return null;
  }
  if (errorCode === "access_denied") {
    throw new Error("User từ chối quyền truy cập");
  }
  if (errorCode === "expired_token") {
    throw new Error("Hết hạn chờ (5 phút). Chạy lại 'avatar login'.");
  }
  throw new Error(`OAuth token endpoint trả lỗi: ${errorCode || res.status}`);
}

// Decode JWT payload WITHOUT verifying signature. Safe here because we receive
// the token directly from Google over HTTPS — no MITM surface.
export function decodeIdToken(idToken: string): IdTokenClaims {
  const parts = idToken.split(".");
  if (parts.length !== 3) {
    throw new Error("id_token format không hợp lệ");
  }
  const payload = parts[1];
  if (!payload) throw new Error("id_token thiếu payload");
  // Convert base64url to base64.
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const json = Buffer.from(base64, "base64").toString("utf8");
  return JSON.parse(json) as IdTokenClaims;
}

// ── Step 6: enforce hosted domain. Reject account ngoài các workspace NAL.
// v1.14.0 SECURITY: Full JWT claims verify (iss + aud + exp + hd + email_verified).
// Trước fix: chỉ check `hd` + `email_verified` → token forged với matching `hd`
// nhưng issuer khác / audience khác / đã expired vẫn pass.
// Sau fix: defense-in-depth — 5 invariants. Token Google sign nhưng signature
// verify để skip (NAL không có private key Google). Claims-only check vẫn block
// most attack vectors khi token đến trực tiếp từ Google endpoint qua HTTPS.

// Issuer canonical Google. Accept cả 2 form: với https:// và không (theo OIDC spec).
const VALID_ISSUERS = new Set(["https://accounts.google.com", "accounts.google.com"]);
// Clock skew tolerance — Google docs khuyến nghị 30-60s, dùng 60s.
const CLOCK_SKEW_SECONDS = 60;

export function verifyIdTokenClaims(claims: IdTokenClaims): void {
  // 1. Issuer phải là Google.
  if (!VALID_ISSUERS.has(claims.iss)) {
    throw new Error(`id_token issuer không hợp lệ: ${claims.iss} (expect: accounts.google.com)`);
  }
  // 2. Audience phải khớp client ID của Avatar.
  if (claims.aud !== GOOGLE_CLIENT_ID) {
    throw new Error(
      "id_token audience không khớp client ID Avatar. Token có thể được sign cho app khác.",
    );
  }
  // 3. Token chưa expire (với clock skew tolerance).
  const nowSec = Math.floor(Date.now() / 1000);
  if (claims.exp + CLOCK_SKEW_SECONDS < nowSec) {
    const ageSec = nowSec - claims.exp;
    throw new Error(`id_token đã hết hạn ${ageSec}s trước. Login lại để lấy token mới.`);
  }
  // 4. Hosted domain restriction NAL — chấp nhận BẤT KỲ workspace nào trong danh
  // sách (nal.vn HOẶC nal-software.com). hd phải tồn tại (account Workspace thật)
  // và nằm trong danh sách cho phép.
  if (!claims.hd || !ALLOWED_HOSTED_DOMAINS.includes(claims.hd)) {
    throw new Error(
      `Email không thuộc workspace NAL (yêu cầu ${ALLOWED_HOSTED_DOMAINS.map((d) => `@${d}`).join(" hoặc ")}). Nhận: ${claims.email}${claims.hd ? ` (hd=${claims.hd})` : " (không phải tài khoản Workspace)"}`,
    );
  }
  // 5. Email Google verified.
  if (!claims.email_verified) {
    throw new Error("Email chưa được Google verify");
  }
}

// Backward-compat alias — existing callers giữ tên cũ. Có thể deprecate sau.
export const verifyHostedDomain = verifyIdTokenClaims;

// Convert OAuth token response + decoded claims into the on-disk UserConfig shape.
export function buildUserConfig(token: TokenResponse, claims: IdTokenClaims): UserConfig {
  const expiresAt = new Date(Date.now() + token.expires_in * 1000).toISOString();
  return {
    email: claims.email,
    name: claims.name ?? claims.email,
    access_token: token.access_token,
    refresh_token: token.refresh_token,
    expires_at: expiresAt,
    id_token: token.id_token,
  };
}

// Refresh flow: exchange refresh_token for a new access_token. Used by other
// commands when they detect an expired token (see isTokenExpired).
export async function refreshAccessToken(refreshToken: string): Promise<{
  access_token: string;
  expires_in: number;
  id_token?: string;
}> {
  const res = await postToAuthBff("refresh", { refresh_token: refreshToken });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Refresh token failed (${res.status}): ${text}`);
  }
  return (await res.json()) as { access_token: string; expires_in: number; id_token?: string };
}

// Revoke a token (used when login is rejected post-hoc, e.g. wrong domain).
export async function revokeToken(token: string): Promise<void> {
  // Timeout 10s — best-effort revoke, không để treo vô hạn nếu BFF không phản hồi.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10_000);
  try {
    // Best-effort: nuốt cả lỗi network lẫn lỗi resolve endpoint (sync throw khi
    // config endpoint sai) — revoke fail KHÔNG được chặn logout (hành vi cũ:
    // hàm này không bao giờ throw).
    await postToAuthBff("revoke", { token }, controller.signal).catch(() => {});
  } catch {
    // resolveAuthDeviceEndpoint throw → vẫn nuốt.
  } finally {
    clearTimeout(timer);
  }
}

// Build verification URL. KHÔNG set hd hint: nhiều workspace NAL (nal.vn +
// nal-software.com) → hint chỉ-1-domain sẽ lọc mất account của workspace kia.
// Để Google hiện mọi account, user tự chọn; verifyIdTokenClaims chặn account
// ngoài ALLOWED_HOSTED_DOMAINS ở bước sau (hint chỉ là UX, không phải bảo mật).
export function buildVerificationUrl(response: DeviceCodeResponse): string {
  const url = new URL(response.verification_url);
  url.searchParams.set("user_code", response.user_code);
  return url.toString();
}
